import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

const JOB_SIZE_BUCKETS = ["0-500", "500-2000", "2000-5000", "5000-10000", ">10000"];

export default function OperatorOutputReport() {
  const [operators, setOperators] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operatorId, setOperatorId] = useState("all");
  const [machineId, setMachineId] = useState("all");
  const [shift, setShift] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/operators").then(({ data }) => setOperators(data)).catch(() => {});
    api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (operatorId !== "all") params.operator_id = operatorId;
    if (machineId !== "all") params.machine_id = machineId;
    if (shift !== "all") params.shift = shift;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/reports/operator-output", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [operatorId, machineId, shift]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const combined = data?.combined_histogram || {};

  const exportCSV = () => downloadCSV(
    "operator-output-report",
    ["Operator", "Machine", "Production Hours", "No. of Jobs", "Setup Waste", "Good Meters", "LSD Meters", "Total Meters",
     "Bucket 0-500", "Bucket 500-2000", "Bucket 2000-5000", "Bucket 5000-10000", "Bucket >10000",
     "Waste %", "Yield %", "Output Efficiency %"],
    rows.map((r) => {
      const h = r.job_size_histogram || {};
      return [
        r.operator_name, r.machine_name, r.production_hours, r.no_of_jobs,
        r.setup_waste, r.good_meters, r.lsd_meters, r.total_meters,
        h["0-500"] || 0, h["500-2000"] || 0, h["2000-5000"] || 0, h["5000-10000"] || 0, h[">10000"] || 0,
        r.waste_pct, r.yield_pct, r.output_efficiency ?? "",
      ];
    })
  );

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Operator</label>
          <Select value={operatorId} onValueChange={setOperatorId}>
            <SelectTrigger data-testid="opout-operator" className="rounded-sm w-56"><SelectValue placeholder="All operators" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">All operators</SelectItem>
              {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Machine</label>
          <Select value={machineId} onValueChange={setMachineId}>
            <SelectTrigger data-testid="opout-machine" className="rounded-sm w-48"><SelectValue placeholder="All machines" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">All machines</SelectItem>
              {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Shift</label>
          <Select value={shift} onValueChange={setShift}>
            <SelectTrigger data-testid="opout-shift" className="rounded-sm w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="A">A</SelectItem>
              <SelectItem value="B">B</SelectItem>
              <SelectItem value="C">C</SelectItem>
              <SelectItem value="General">General</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">From</label>
          <Input data-testid="opout-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">To</label>
          <Input data-testid="opout-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" />
        </div>
        <Button data-testid="opout-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="opout-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi label="Production hours" value={inr(totals.production_hours || 0)} suffix="h" />
        <Kpi label="Jobs" value={inr(totals.no_of_jobs || 0)} />
        <Kpi label="Good meters" value={inr(totals.good_meters || 0)} tone="ok" suffix="m" />
        <Kpi label="Setup waste" value={inr(totals.setup_waste || 0)} tone="warn" suffix="m" />
        <Kpi label="LSD meters" value={inr(totals.lsd_meters || 0)} tone="warn" suffix="m" />
        <Kpi label="Waste %" value={`${totals.waste_pct || 0}%`}
          tone={totals.waste_pct > 5 ? "danger" : totals.waste_pct > 2 ? "warn" : "ok"} />
      </div>

      {/* Combined job-size histogram */}
      <div className="border border-neutral-200 bg-white p-4 mb-4">
        <div className="label-caps mb-2 text-[10px]">Job-size distribution (meters per job)</div>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-sm">
          {JOB_SIZE_BUCKETS.map((b) => (
            <div key={b} className="p-2 border border-neutral-200 bg-neutral-50" data-testid={`opout-hist-${b}`}>
              <div className="label-caps text-[10px] text-neutral-500">{b} m</div>
              <div className="font-mono-num text-lg font-semibold text-neutral-900">{inr(combined[b] || 0)}</div>
              <div className="text-[10px] text-neutral-500">jobs</div>
            </div>
          ))}
        </div>
      </div>

      {/* Row table with per-row histogram */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="opout-table">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-3 py-2.5 border-r border-neutral-200" colSpan={4}>Operator</th>
              <th className="text-right px-3 py-2.5" colSpan={4}>Volumes (meters)</th>
              <th className="text-center px-3 py-2.5 border-l border-neutral-200" colSpan={5}>Job-size histogram (jobs per bucket)</th>
              <th className="text-right px-3 py-2.5 border-l border-neutral-200" colSpan={3}>Efficiency</th>
            </tr>
            <tr className="border-t border-neutral-200">
              <th className="text-left px-3 py-2">Operator</th>
              <th className="text-left px-3 py-2">Machine</th>
              <th className="text-right px-3 py-2">Prod Hrs</th>
              <th className="text-right px-3 py-2 border-r border-neutral-200">Jobs</th>
              <th className="text-right px-3 py-2">Setup Waste</th>
              <th className="text-right px-3 py-2">Good</th>
              <th className="text-right px-3 py-2">LSD</th>
              <th className="text-right px-3 py-2">Total</th>
              {JOB_SIZE_BUCKETS.map((b) => (
                <th key={b} className="text-right px-2 py-2 border-l border-neutral-100">{b}</th>
              ))}
              <th className="text-right px-3 py-2 border-l border-neutral-200">Waste %</th>
              <th className="text-right px-3 py-2">Yield %</th>
              <th className="text-right px-3 py-2">Output Eff %</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={16} msg="No production logs in this filter." />}
            {rows.map((r, i) => {
              const wTone = r.waste_pct >= 5 ? "text-rose-700 font-bold"
                : r.waste_pct >= 2 ? "text-amber-700 font-semibold"
                : "text-emerald-700";
              return (
                <tr key={`${r.operator_id}-${r.machine_id}-${i}`}
                  className="border-t border-neutral-100 data-row"
                  data-testid={`opout-row-${r.operator_name}-${r.machine_name}`}>
                  <td className="px-3 py-2 font-medium">{r.operator_name}</td>
                  <td className="px-3 py-2 text-xs text-neutral-700">{r.machine_name}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.production_hours}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold border-r border-neutral-100">{r.no_of_jobs}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-amber-700">{inr(r.setup_waste)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{inr(r.good_meters)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-amber-700">{inr(r.lsd_meters)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.total_meters)}</td>
                  {JOB_SIZE_BUCKETS.map((b) => {
                    const cnt = r.job_size_histogram?.[b] || 0;
                    return (
                      <td key={b} className={`px-2 py-2 text-right font-mono-num border-l border-neutral-100 ${cnt === 0 ? "text-neutral-300" : "text-neutral-800 font-semibold"}`}>
                        {cnt || "—"}
                      </td>
                    );
                  })}
                  <td className={`px-3 py-2 text-right font-mono-num border-l border-neutral-200 ${wTone}`}>{r.waste_pct}%</td>
                  <td className="px-3 py-2 text-right font-mono-num text-neutral-700">{r.yield_pct}%</td>
                  <td className="px-3 py-2 text-right font-mono-num text-neutral-700">
                    {r.output_efficiency != null ? `${r.output_efficiency}%` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Waste % = (Setup + LSD) / Total Meters × 100. Yield % = Good / Total × 100.
        Output Efficiency = Good Meters / (Machine Capacity × Production Hours) × 100 — shown only when machine capacity is set.
      </p>
    </div>
  );
}


// ============================================================================
// 10. OTIF (Late Delivery %) Report
// ============================================================================
